import React, { useState, useRef, useEffect } from 'react';
import { BrowserQRCodeReader } from '@zxing/browser';
import { SyncLoader } from 'react-spinners';

const QRCodeScanner = ({ onQRCodeRead, isVisible, onClose, resetTrigger }) => {
    const [isScanning, setIsScanning] = useState(false);
    const [cameraError, setCameraError] = useState(null);
    const qrReaderRef = useRef(null);
    const videoRef = useRef(null);
    const isReadingRef = useRef(false); // QRコード読み取り中フラグ
    const currentStreamRef = useRef(null); // 現在のストリームを保存
    const isVisibleRef = useRef(isVisible); // isVisibleの現在値を保持
    const lastReadCodeRef = useRef(null); // 最後に読み取ったコードを記録

    // resetTriggerが変更されたときに状態をリセット
    useEffect(() => {
        if (resetTrigger !== undefined && lastReadCodeRef.current) {
            console.log('QRスキャナーの状態をリセット');
            lastReadCodeRef.current = null;
        }
    }, [resetTrigger]);

    // QRコードスキャンを開始
    const startQrScan = async () => {
        try {
            setCameraError(null);
            setIsScanning(false);
            isReadingRef.current = false; // 読み取り中フラグをリセット
            
            // 既存のストリームがあれば停止
            if (currentStreamRef.current) {
                currentStreamRef.current.getTracks().forEach(track => track.stop());
                currentStreamRef.current = null;
            }
            
            // 既存のリーダーがある場合は適切に停止
            if (qrReaderRef.current) {
                try {
                    if (typeof qrReaderRef.current.stopContinuousDecode === 'function') {
                        qrReaderRef.current.stopContinuousDecode();
                    }
                } catch (e) {
                    console.log('既存リーダーの停止中にエラー（無視）:', e);
                }
                qrReaderRef.current = null;
            }
            
            // 新しいQRコードリーダーを作成
            qrReaderRef.current = new BrowserQRCodeReader();
            
            // navigator.mediaDevicesを使用してデバイス一覧を取得
            const allDevices = await navigator.mediaDevices.enumerateDevices();
            const videoInputDevices = allDevices.filter(device => device.kind === 'videoinput');
            console.log('カメラデバイス一覧:', videoInputDevices);
            
            if (videoInputDevices.length === 0) {
                throw new Error('カメラデバイスが見つかりません');
            }

            setIsScanning(true);

            // 背面カメラを優先的に選択
            let selectedDeviceId = videoInputDevices[0].deviceId;
            const backCamera = videoInputDevices.find(device => 
                device.label.toLowerCase().includes('back') || 
                device.label.toLowerCase().includes('rear') ||
                device.label.toLowerCase().includes('environment')
            );
            if (backCamera) {
                selectedDeviceId = backCamera.deviceId;
            }
            
            console.log('使用するカメラ:', selectedDeviceId);
            
            // スキャン開始 - ZXingに直接ストリーム管理を任せる
            await qrReaderRef.current.decodeFromVideoDevice(selectedDeviceId, videoRef.current, (result, error) => {
                // コンポーネントが非表示になっていたら何もしない
                if (!isVisibleRef.current || isReadingRef.current) {
                    return;
                }
                
                if (result) {
                    const qrCode = result.getText();
                    
                    // 同じコードを連続で読み取らないようにチェック
                    if (lastReadCodeRef.current === qrCode) {
                        return;
                    }
                    
                    isReadingRef.current = true; // 読み取り中フラグを設定
                    lastReadCodeRef.current = qrCode; // 最後に読み取ったコードを記録
                    console.log('QRコード読み取り成功:', qrCode);
                    
                    // ストリームを保存（停止用）
                    if (videoRef.current && videoRef.current.srcObject) {
                        currentStreamRef.current = videoRef.current.srcObject;
                    }
                    
                    // コールバックを実行してからクリーンアップ
                    onQRCodeRead(qrCode);
                    
                    // 少し遅延してから停止・クローズ
                    setTimeout(() => {
                        if (qrReaderRef.current) {
                            stopQrScan();
                        }
                        onClose();
                    }, 50);
                }
                
                // NotFoundException（QRコードが見つからない）とChecksumException（読み取りエラー）は無視
                if (error && 
                    !(error.name === 'NotFoundException') && 
                    !(error.name === 'ChecksumException') &&
                    !(error instanceof Error && error.message.includes('ChecksumException'))) {
                    console.log('QR読み取りエラー:', error);
                }
            });
            
            // スキャン開始後にストリームを保存
            setTimeout(() => {
                if (videoRef.current && videoRef.current.srcObject) {
                    currentStreamRef.current = videoRef.current.srcObject;
                }
            }, 1000);
            
        } catch (err) {
            console.error('カメラアクセスエラー:', err);
            setIsScanning(false);
            
            if (err.name === 'NotAllowedError') {
                setCameraError('カメラの使用が拒否されました。ブラウザの設定でカメラの使用を許可してください。');
            } else if (err.name === 'NotFoundError') {
                setCameraError('カメラデバイスが見つかりません。カメラが接続されているか確認してください。');
            } else if (err.name === 'NotSupportedError') {
                setCameraError('このブラウザではカメラ機能がサポートされていません。');
            } else if (err.name === 'NotReadableError') {
                setCameraError('カメラが他のアプリケーションで使用中の可能性があります。');
            } else {
                setCameraError('カメラにアクセスできません: ' + err.message);
            }
        }
    };

    // QRコードスキャンを停止
    const stopQrScan = () => {
        try {
            console.log('カメラ停止処理開始');
            
            // 読み取り中フラグをリセット
            isReadingRef.current = false;
            
            // ZXingリーダーを停止
            if (qrReaderRef.current) {
                console.log('ZXingリーダーを停止中...');
                try {
                    if (typeof qrReaderRef.current.stopContinuousDecode === 'function') {
                        qrReaderRef.current.stopContinuousDecode();
                    }
                    if (typeof qrReaderRef.current.reset === 'function') {
                        qrReaderRef.current.reset();
                    }
                } catch (e) {
                    console.log('ZXingリーダー停止中にエラー:', e);
                }
                qrReaderRef.current = null;
            }
            
            // 保存されたストリームを停止
            if (currentStreamRef.current) {
                console.log('保存されたストリームを停止中...');
                const tracks = currentStreamRef.current.getTracks();
                tracks.forEach(track => {
                    track.stop();
                    console.log('トラック停止:', track.kind, track.readyState);
                });
                currentStreamRef.current = null;
            }
            
            // ビデオ要素からストリームを停止
            if (videoRef.current && videoRef.current.srcObject) {
                console.log('ビデオ要素のストリームを停止中...');
                const stream = videoRef.current.srcObject;
                const tracks = stream.getTracks();
                tracks.forEach(track => {
                    track.stop();
                    console.log('ビデオトラック停止:', track.kind, track.readyState);
                });
                videoRef.current.srcObject = null;
            }
            
            setIsScanning(false);
            setCameraError(null);
            console.log('カメラ停止処理完了');
            
        } catch (err) {
            console.error('カメラ停止エラー:', err);
            setIsScanning(false);
            setCameraError(null);
        }
    };

    // コンポーネントアンマウント時のクリーンアップ
    useEffect(() => {
        return () => {
            stopQrScan();
        };
    }, []);

    // isVisibleの値を常に最新に保つ
    useEffect(() => {
        isVisibleRef.current = isVisible;
    }, [isVisible]);

    // 表示状態が変わったときの処理
    useEffect(() => {
        console.log('isVisible changed:', isVisible, 'isScanning:', isScanning, 'isReading:', isReadingRef.current);
        
        // QRコード読み取り中は何もしない
        if (isReadingRef.current) {
            console.log('読み取り中のため処理をスキップ');
            return;
        }
        
        if (isVisible && !isScanning && !cameraError) {
            console.log('QRスキャン開始をリクエスト');
            startQrScan();
        } else if (!isVisible) {
            console.log('QRスキャン停止をリクエスト');
            stopQrScan();
        }
        // startQrScan/stopQrScan/isScanning/cameraErrorをすべて依存に含めると、
        // カメラ起動処理自体が状態更新のたびに再実行され、カメラの開始/停止が繰り返される恐れがあるため、
        // isVisibleの変化時にのみ発火する意図的な設計とする。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]); // isVisible のみを監視

    if (!isVisible) return null;

    return (
        <div className='mt-3 p-4 border border-gray-300 rounded-lg bg-gray-50'>
            <div className='text-center mb-3'>
                <p className='text-gray-600 mb-2'>QRコードスキャン</p>
            </div>
            
            {cameraError ? (
                <div className='bg-red-50 border border-red-200 rounded-lg p-4 text-center'>
                    <i className='bi bi-exclamation-triangle text-red-500 text-2xl mb-2'></i>
                    <p className='text-red-600 text-sm mb-3'>{cameraError}</p>
                    
                    <div className='text-left text-xs text-gray-600 bg-gray-100 p-2 rounded mb-3'>
                        <p className='font-semibold mb-1'>トラブルシューティング:</p>
                        <ul className='list-disc list-inside space-y-1'>
                            <li>ブラウザのアドレスバーの左側にあるカメラアイコンをクリックして許可してください</li>
                            <li>他のアプリでカメラを使用していないか確認してください</li>
                            <li>ページを再読み込みしてみてください</li>
                            <li>HTTPSでアクセスしているか確認してください（localhostは除く）</li>
                        </ul>
                    </div>
                    
                    <button 
                        type='button'
                        className='bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm'
                        onClick={() => {
                            setCameraError(null);
                            startQrScan();
                        }}
                    >
                        <i className='bi bi-arrow-clockwise me-1'></i>
                        再試行
                    </button>
                </div>
            ) : (
                <div className='relative bg-black rounded-lg overflow-hidden'>
                    <video
                        ref={videoRef}
                        className='w-full h-64 object-cover'
                        style={{ transform: 'scaleX(-1)' }} // 鏡像表示
                        playsInline
                        muted
                    />
                    
                    {isScanning && (
                        <div className='absolute inset-0 flex items-center justify-center'>
                            <div className='border-4 border-green-400 w-56 h-56 rounded-lg shadow-lg'>
                                {/* コーナー装飾 */}
                                <div className='absolute -top-2 -left-2 w-8 h-8 border-l-4 border-t-4 border-white'></div>
                                <div className='absolute -top-2 -right-2 w-8 h-8 border-r-4 border-t-4 border-white'></div>
                                <div className='absolute -bottom-2 -left-2 w-8 h-8 border-l-4 border-b-4 border-white'></div>
                                <div className='absolute -bottom-2 -right-2 w-8 h-8 border-r-4 border-b-4 border-white'></div>
                            </div>
                            
                            {/* スキャン中のライン */}
                            <div className='absolute top-0 left-1/2 transform -translate-x-1/2 w-48 h-1 bg-green-400 shadow-lg'
                                 style={{
                                     animation: 'scanLine 2s linear infinite',
                                     boxShadow: '0 0 10px #4ade80'
                                 }}></div>
                            
                            {/* アニメーション用のCSSスタイル */}
                            <style jsx>{`
                                @keyframes scanLine {
                                    0% {
                                        top: 20%;
                                        opacity: 1;
                                    }
                                    50% {
                                        opacity: 0.8;
                                    }
                                    100% {
                                        top: 80%;
                                        opacity: 1;
                                    }
                                }
                            `}</style>
                        </div>
                    )}
                    
                    {!isScanning && (
                        <div className='absolute inset-0 flex items-center justify-center bg-black bg-opacity-50'>
                            <div className='text-center text-white'>
                                <SyncLoader color="#ffffff" size={8} />
                                <p className='mt-2 text-sm'>カメラを起動中...</p>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default QRCodeScanner;
